import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom, timeout } from 'rxjs';
import { environment } from '../../../environments/environment';

interface DirectusResponse<T> {
  data: T;
}

export interface DirectusCollectionMeta {
  collection: string;
  meta: { icon?: string; note?: string; hidden?: boolean; singleton?: boolean } | null;
}

export interface DirectusFieldMeta {
  collection: string;
  field: string;
  type: string;
  schema: { foreign_key_table?: string; is_primary_key?: boolean } | null;
  meta: { interface?: string; special?: string[]; hidden?: boolean } | null;
}

export interface AuthProvider {
  name: string;
  icon?: string;
  label?: string;
  driver?: string;
}

@Injectable({ providedIn: 'root' })
export class DirectusService {
  private readonly baseUrl = environment.directusUrl.replace(/\/$/, '');
  private readonly tokenKey = 'officer_access_token';
  private readonly requestTimeoutMs = 10000;
  /** Read-only service token used as fallback when the officer role lacks access to a collection. */
  private readonly membersToken: string = (environment as Record<string, unknown>)['directusMembersToken'] as string || '';

  constructor(private readonly http: HttpClient) {}

  get token(): string {
    return localStorage.getItem(this.tokenKey) || '';
  }

  setToken(token: string): void {
    localStorage.setItem(this.tokenKey, token);
  }

  clearToken(): void {
    localStorage.removeItem(this.tokenKey);
  }

  ssoProviderFromConfig(): string {
    return String(environment.authProvider || '').trim();
  }

  buildSsoLoginUrl(provider: string, redirectUrl: string): string {
    const encodedProvider = encodeURIComponent(provider);
    const encodedRedirect = encodeURIComponent(redirectUrl);
    return `${this.baseUrl}/auth/login/${encodedProvider}?redirect=${encodedRedirect}`;
  }

  private headers(): HttpHeaders {
    let headers = new HttpHeaders();
    if (this.token) headers = headers.set('Authorization', `Bearer ${this.token}`);
    return headers;
  }

  private headersForToken(token: string): HttpHeaders {
    let headers = new HttpHeaders();
    if (token) headers = headers.set('Authorization', `Bearer ${token}`);
    return headers;
  }

  private params(record: Record<string, string | number | boolean | undefined>): HttpParams {
    let params = new HttpParams();
    for (const [key, value] of Object.entries(record)) {
      if (value === undefined || value === null || value === '') continue;
      params = params.set(key, String(value));
    }
    return params;
  }

  async listAuthProviders(): Promise<AuthProvider[]> {
    const response = await firstValueFrom(
      this.http.get<DirectusResponse<AuthProvider[]>>(`${this.baseUrl}/auth/providers`, {
        headers: this.headers(),
        withCredentials: true
      })
    );

    return response.data || [];
  }

  async exchangeSsoCode(
    provider: string,
    callbackParams: Record<string, string>
  ): Promise<string> {
    const params = this.params({ ...callbackParams, mode: 'json' });
    const response = await firstValueFrom(
      this.http.get<DirectusResponse<{ access_token?: string }>>(
        `${this.baseUrl}/auth/login/${encodeURIComponent(provider)}/callback`,
        {
          headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
          params,
          withCredentials: true
        }
      )
    );

    return response?.data?.access_token || '';
  }

  async me(): Promise<Record<string, unknown>> {
    try {
      const response = await firstValueFrom(
        this.http.get<DirectusResponse<Record<string, unknown>>>(`${this.baseUrl}/users/me`, {
          headers: this.headers(),
          params: this.params({ fields: 'id,first_name,last_name,email,role.name' }),
          withCredentials: true
        }).pipe(timeout(this.requestTimeoutMs))
      );
      return response.data || {};
    } catch {
      const fallback = await firstValueFrom(
        this.http.get<DirectusResponse<Record<string, unknown>>>(`${this.baseUrl}/users/me`, {
          headers: this.headers(),
          withCredentials: true
        }).pipe(timeout(this.requestTimeoutMs))
      );
      return fallback.data || {};
    }
  }

  /**
   * Collections the service token is allowed to read on behalf of the officer role.
   * This list should shrink to zero once Directus permissions are fixed properly.
   */
  private readonly serviceTokenCollections = new Set([
    'profiles',
    'events',
    'event_attendees',
    'event_orders',
    'event_order_items',
    'event_tickets',
    'eventcategories',
    'guest_contacts',
    'experiences',
    'lbodmembers',
    'member_tags',
    'member_points',
    'membership_history',
  ]);

  async list(
    collection: string,
    query: Record<string, string | number | boolean | undefined> = {}
  ): Promise<Record<string, unknown>[]> {
    const url = `${this.baseUrl}/items/${encodeURIComponent(collection)}`;
    const queryParams = this.params(query);
    try {
      const response = await firstValueFrom(
        this.http.get<DirectusResponse<Record<string, unknown>[]>>(
          url, { headers: this.headers(), params: queryParams, withCredentials: true }
        )
      );
      return response.data || [];
    } catch (err: unknown) {
      // Only fall back to the service token for explicitly whitelisted collections
      if ((err as { status?: number })?.status === 403
          && this.membersToken
          && this.serviceTokenCollections.has(collection)) {
        const response = await firstValueFrom(
          this.http.get<DirectusResponse<Record<string, unknown>[]>>(
            url, { headers: this.headersForToken(this.membersToken), params: queryParams }
          )
        );
        return response.data || [];
      }
      throw err;
    }
  }

  async getSingleton(
    collection: string,
    query: Record<string, string | number | boolean | undefined> = {}
  ): Promise<Record<string, unknown>> {
    const url = `${this.baseUrl}/items/${encodeURIComponent(collection)}`;
    const queryParams = this.params(query);
    try {
      const response = await firstValueFrom(
        this.http.get<DirectusResponse<Record<string, unknown>>>(
          url, { headers: this.headers(), params: queryParams, withCredentials: true }
        )
      );
      return response.data || {};
    } catch (err: unknown) {
      if ((err as { status?: number })?.status === 403
          && this.membersToken
          && this.serviceTokenCollections.has(collection)) {
        const response = await firstValueFrom(
          this.http.get<DirectusResponse<Record<string, unknown>>>(
            url, { headers: this.headersForToken(this.membersToken), params: queryParams }
          )
        );
        return response.data || {};
      }
      throw err;
    }
  }

  async create(collection: string, payload: Record<string, unknown>): Promise<Record<string, unknown>> {
    const response = await firstValueFrom(
      this.http.post<DirectusResponse<Record<string, unknown>>>(
        `${this.baseUrl}/items/${encodeURIComponent(collection)}`,
        payload,
        { headers: this.headers(), withCredentials: true }
      )
    );

    return response.data;
  }

  async update(
    collection: string,
    id: string | number,
    payload: Record<string, unknown>
  ): Promise<Record<string, unknown>> {
    const response = await firstValueFrom(
      this.http.patch<DirectusResponse<Record<string, unknown>>>(
        `${this.baseUrl}/items/${encodeURIComponent(collection)}/${encodeURIComponent(String(id))}`,
        payload,
        { headers: this.headers(), withCredentials: true }
      )
    );

    return response.data;
  }

  async remove(collection: string, id: string | number): Promise<void> {
    await firstValueFrom(
      this.http.delete(`${this.baseUrl}/items/${encodeURIComponent(collection)}/${encodeURIComponent(String(id))}`, {
        headers: this.headers(),
        withCredentials: true
      })
    );
  }

  async uploadFile(
    file: File,
    metadata: Record<string, string | number | boolean | undefined> = {}
  ): Promise<Record<string, unknown>> {
    const form = new FormData();
    form.append('file', file, file.name);
    for (const [key, value] of Object.entries(metadata)) {
      if (value === undefined || value === null || value === '') continue;
      form.append(key, String(value));
    }

    const response = await firstValueFrom(
      this.http.post<DirectusResponse<Record<string, unknown>>>(
        `${this.baseUrl}/files`,
        form,
        { headers: this.headers(), withCredentials: true }
      )
    );

    return response.data || {};
  }

  async removeFile(id: string): Promise<void> {
    await firstValueFrom(
      this.http.delete(`${this.baseUrl}/files/${encodeURIComponent(id)}`, {
        headers: this.headers(),
        withCredentials: true
      })
    );
  }

  buildAssetUrl(
    fileId: string,
    query: Record<string, string | number | boolean | undefined> = {}
  ): string {
    const params = this.params({
      ...query,
      access_token: this.token || undefined
    });
    const qs = params.toString();
    return `${this.baseUrl}/assets/${encodeURIComponent(fileId)}${qs ? `?${qs}` : ''}`;
  }

  /**
   * Build an asset URL using a specific saved crop region (Sharp extract + resize),
   * matching the same transform approach used by the website frontend.
   * Falls back to simple cover-fit when no crop data is provided.
   */
  croppedAssetUrl(
    fileId: string,
    crop: { left: number; top: number; width: number; height: number } | null | undefined,
    targetW: number,
    targetH: number
  ): string {
    if (!crop) {
      return this.buildAssetUrl(fileId, { width: targetW, height: targetH, fit: 'cover' });
    }
    const transforms = JSON.stringify([
      ['extract', { left: crop.left, top: crop.top, width: crop.width, height: crop.height }],
      ['resize', { width: targetW, height: targetH, fit: 'cover' }]
    ]);
    return this.buildAssetUrl(fileId, { transforms, format: 'webp', quality: 85 });
  }

  async listFlows(
    query: Record<string, string | number | boolean | undefined> = {}
  ): Promise<Record<string, unknown>[]> {
    const response = await firstValueFrom(
      this.http.get<DirectusResponse<Record<string, unknown>[]>>(`${this.baseUrl}/flows`, {
        headers: this.headers(),
        params: this.params(query),
        withCredentials: true
      })
    );
    return response.data || [];
  }

  async updateFlow(id: string, payload: Record<string, unknown>): Promise<Record<string, unknown>> {
    const response = await firstValueFrom(
      this.http.patch<DirectusResponse<Record<string, unknown>>>(
        `${this.baseUrl}/flows/${encodeURIComponent(id)}`,
        payload,
        { headers: this.headers(), withCredentials: true }
      )
    );
    return response.data;
  }

  async listCollections(): Promise<DirectusCollectionMeta[]> {
    const response = await firstValueFrom(
      this.http.get<DirectusResponse<DirectusCollectionMeta[]>>(
        `${this.baseUrl}/collections`,
        { headers: this.headers(), withCredentials: true }
      ).pipe(timeout(this.requestTimeoutMs))
    );
    return response.data || [];
  }

  async listFields(collection: string): Promise<DirectusFieldMeta[]> {
    const response = await firstValueFrom(
      this.http.get<DirectusResponse<DirectusFieldMeta[]>>(
        `${this.baseUrl}/fields/${encodeURIComponent(collection)}`,
        { headers: this.headers(), withCredentials: true }
      ).pipe(timeout(this.requestTimeoutMs))
    );
    return response.data || [];
  }

  async listActivity(
    query: Record<string, string | number | boolean | undefined> = {}
  ): Promise<Record<string, unknown>[]> {
    const response = await firstValueFrom(
      this.http.get<DirectusResponse<Record<string, unknown>[]>>(`${this.baseUrl}/activity`, {
        headers: this.headers(),
        params: this.params(query),
        withCredentials: true
      })
    );

    return response.data || [];
  }

  async triggerFlow(
    flowId: string,
    payload: Record<string, unknown> = {}
  ): Promise<Record<string, unknown>> {
    const response = await firstValueFrom(
      this.http.post<Record<string, unknown> | DirectusResponse<Record<string, unknown>>>(
        `${this.baseUrl}/flows/trigger/${encodeURIComponent(flowId)}`,
        payload,
        { headers: this.headers(), withCredentials: true }
      )
    );

    const wrapped = response as DirectusResponse<Record<string, unknown>>;
    if (wrapped && typeof wrapped === 'object' && 'data' in wrapped) {
      return wrapped.data || {};
    }
    return (response as Record<string, unknown>) || {};
  }
}
